import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ShoppingBag,
  Plus,
  Minus,
  Trash2,
  Wallet,
  ArrowLeft,
  Search,
  Home,
  Check,
  Loader2,
  LogOut,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useProducts } from '@/hooks/useProducts';
import { useCurrency } from '@/hooks/useCurrency';
import { useOrders } from '@/hooks/useOrders';
import { toast } from 'sonner';
import { useCustomerKiosk, useWalletPayment, useKioskLocation, useKioskOrganization } from '@/hooks/useCustomerKiosk';
import { cn } from '@/lib/utils';

interface CartItem {
  id: string;
  product_id: string;
  name: string;
  price: number;
  quantity: number;
  image_url?: string | null;
}

type KioskView = 'categories' | 'products' | 'cart' | 'checkout' | 'confirmation';

const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export default function CustomerKiosk() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get organization and location from URL params (can be UUID or slug/code)
  const orgParam = searchParams.get('org');
  const locParam = searchParams.get('loc');

  // Resolve organization first (by UUID or slug)
  const { data: organization, isLoading: orgLoading } = useKioskOrganization(orgParam);
  const organizationId = organization?.id || null;

  // Customer session (uses resolved org ID)
  const {
    session,
    isAuthenticated,
    isInitialized,
    refreshBalance,
    updateBalance,
    extendSession,
    logout,
    isSessionExpired: _isSessionExpired,
  } = useCustomerKiosk(organizationId);

  // Location info (resolve by UUID or code using org ID)
  const { data: location } = useKioskLocation(locParam, organizationId);
  const locationId = location?.id || null;
  const taxRate = location?.default_tax_rate ? Number(location.default_tax_rate) : 0.15;

  // Products
  const { products, categories, isLoading } = useProducts({ locationId });
  const { formatCurrency } = useCurrency();
  const { createOrderAndSendToKitchen, addPayment, completeOrder } = useOrders(locationId, { disableRealtime: true });
  const { processPayment, isProcessing } = useWalletPayment();

  // State
  const [view, setView] = useState<KioskView>('categories');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Login URL for redirects (preserve original URL params)
  const loginUrl = locParam
    ? `/customer-kiosk?org=${orgParam}&loc=${locParam}`
    : `/customer-kiosk?org=${orgParam}`;

  // Handle logout - defined early so it can be used in effects
  const handleLogout = useCallback(() => {
    logout();
    navigate(loginUrl);
  }, [logout, navigate, loginUrl]);

  // Redirect to login if not authenticated (wait for org to load first)
  useEffect(() => {
    // Don't redirect while org is still loading or session hasn't been checked
    if (orgLoading || !organizationId || !isInitialized) {
      return;
    }
    
    if (!isAuthenticated || !session) {
      navigate(loginUrl, { replace: true });
    }
  }, [isInitialized, isAuthenticated, session, navigate, loginUrl, orgLoading, organizationId]);

  // Inactivity timer - logout after timeout
  // Use a boolean to only re-run when login status changes, not on every session update
  const hasSession = !!session;
  
  useEffect(() => {
    if (!hasSession) return;

    const resetTimer = () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }

      extendSession();

      inactivityTimerRef.current = setTimeout(() => {
        toast.success('Session Timeout', {
        description: 'Your session has expired due to inactivity',
      });
        handleLogout();
      }, INACTIVITY_TIMEOUT_MS);
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'touchstart', 'scroll'];
    events.forEach(event => {
      document.addEventListener(event, resetTimer);
    });

    resetTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [hasSession, extendSession, toast, handleLogout]);

  // Filter products
  const filteredProducts = useMemo(() => {
    let filtered = products || [];

    if (selectedCategory) {
      filtered = filtered.filter(p => p.category_id === selectedCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      );
    }

    // Only show available products with positive prices
    filtered = filtered.filter(p => p.is_available && p.price > 0);

    return filtered;
  }, [products, selectedCategory, searchQuery]);

  // Cart calculations
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const tax = subtotal * taxRate;
  const total = subtotal + tax;

  // Check if wallet has sufficient balance
  const walletBalance = session?.walletBalance || 0;
  const hasSufficientBalance = walletBalance >= total;

  // Add to cart
  const addToCart = useCallback((product: typeof products[0]) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        return prev.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [
        ...prev,
        {
          id: crypto.randomUUID(),
          product_id: product.id,
          name: product.name,
          price: product.effective_price ?? product.price,
          quantity: 1,
          image_url: product.image_url,
        },
      ];
    });

    toast('Added to cart', {
        description: 'product.name',
        duration: 1500,
      });
  }, [toast]);

  // Update quantity
  const updateQuantity = useCallback((itemId: string, delta: number) => {
    setCart(prev =>
      prev
        .map(item =>
          item.id === itemId
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item
        )
        .filter(item => item.quantity > 0)
    );
  }, []);

  // Remove item
  const removeItem = useCallback((itemId: string) => {
    setCart(prev => prev.filter(item => item.id !== itemId));
  }, []);

  // Clear cart
  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  // Submit order with wallet payment
  const submitOrder = async () => {
    if (cart.length === 0 || !locationId || !session) return;
    if (!hasSufficientBalance) {
      toast.error('Insufficient Balance', {
        description: 'Please top up your wallet at the counter',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Refresh balance first to ensure it's current
      const currentBalance = await refreshBalance();
      if (currentBalance !== null && currentBalance < total) {
        toast.error('Insufficient Balance', {
        description: 'Your wallet balance has changed. Please top up at the counter.',
      });
        setIsSubmitting(false);
        return;
      }

      // Step 1: Create order
      const orderResult = await createOrderAndSendToKitchen.mutateAsync({
        order_type: 'takeout',
        customer_id: session.customerId || undefined,
        items: cart.map(item => ({
          product_id: item.product_id,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.price,
        })),
      });

      if (!orderResult?.order?.id) {
        throw new Error('Failed to create order');
      }

      const orderId = orderResult.order.id;

      // Step 2: Process wallet payment
      const paymentResult = await processPayment.mutateAsync({
        customerAccountId: session.accountId,
        orderId,
        amount: total,
      });

      // Update local balance
      updateBalance(paymentResult.newBalance);

      // Step 3: Record payment in order
      await addPayment.mutateAsync({
        orderId,
        paymentMethod: 'store_credit', // Using store_credit for wallet payments
        amount: total,
        tipAmount: 0,
      });

      // Step 4: Complete order
      await completeOrder.mutateAsync({ orderId });

      // Show confirmation
      setOrderNumber(orderResult.order.order_number || 'N/A');
      setView('confirmation');
      clearCart();

      // Play success sound
      try {
        const audio = new Audio('/sounds/payment-success.mp3');
        audio.volume = 0.5;
        audio.play().catch(() => {});
      } catch {
        // Sound not available
      }
    } catch (error: any) {
      toast.error('Order Failed', {
        description: error?.message || 'Please try again or ask for assistance',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset to start
  const startOver = useCallback(() => {
    setView('categories');
    setSelectedCategory(null);
    setSearchQuery('');
    setOrderNumber(null);
    clearCart();
  }, [clearCart]);

  // Select category
  const selectCategory = useCallback((categoryId: string) => {
    setSelectedCategory(categoryId);
    setView('products');
  }, []);

  // Loading states - wait for org to load and session to be initialized
  if (orgLoading || !organizationId || !isInitialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 text-primary animate-spin" />
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  // Confirmation view
  if (view === 'confirmation') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center p-8">
        <div className="text-center text-white max-w-lg">
          <div className="w-24 h-24 mx-auto bg-white rounded-full flex items-center justify-center mb-8">
            <Check className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Order Confirmed!</h1>
          <p className="text-xl mb-8 opacity-90">Payment successful - your order is being prepared</p>
          <div className="bg-white/20 rounded-2xl p-8 mb-8">
            <p className="text-lg opacity-80">Order Number</p>
            <p className="text-6xl font-bold mt-2">{orderNumber}</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4 mb-8">
            <p className="text-sm opacity-80">Remaining Wallet Balance</p>
            <p className="text-2xl font-bold">{formatCurrency(session?.walletBalance || 0)}</p>
          </div>
          <p className="text-lg mb-8 opacity-80">Please wait for your number to be called</p>
          <div className="flex gap-4 justify-center">
            <Button
              size="lg"
              variant="secondary"
              className="text-lg h-14 px-8"
              onClick={startOver}
            >
              <Home className="h-5 w-5 mr-2" />
              New Order
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg h-14 px-8 text-white border-white/30 hover:bg-white/10"
              onClick={handleLogout}
            >
              <LogOut className="h-5 w-5 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Customer name display
  const customerName = session
    ? `${session.firstName || ''} ${session.lastName || ''}`.trim() || session.username
    : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex">
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-black/30 backdrop-blur-sm px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {(view === 'products' || view === 'cart' || view === 'checkout') && (
              <Button
                variant="ghost"
                size="icon"
                className="text-white"
                onClick={() => {
                  if (view === 'products') {
                    setView('categories');
                    setSelectedCategory(null);
                  } else if (view === 'cart' || view === 'checkout') {
                    setView('products');
                  }
                }}
              >
                <ArrowLeft className="h-6 w-6" />
              </Button>
            )}
            <div>
              <h1 className="text-2xl font-bold text-white">
                {location?.name || 'Self-Order Kiosk'}
              </h1>
              <p className="text-white/60 text-sm">
                {view === 'categories' && 'Select a category to start'}
                {view === 'products' && categories?.find(c => c.id === selectedCategory)?.name}
                {view === 'cart' && 'Review your order'}
                {view === 'checkout' && 'Complete your order'}
              </p>
            </div>
          </div>

          {/* User Info & Wallet Balance */}
          <div className="flex items-center gap-4">
            {/* Search */}
            {(view === 'categories' || view === 'products') && (
              <div className="relative w-64">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/50" />
                <Input
                  placeholder="Search menu..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (e.target.value && view === 'categories') {
                      setView('products');
                    }
                  }}
                  className="pl-12 h-10 bg-white/10 border-white/20 text-white placeholder:text-white/50"
                />
              </div>
            )}

            {/* Wallet Balance */}
            <div className="bg-white/10 rounded-lg px-4 py-2 flex items-center gap-2">
              <Wallet className="h-5 w-5 text-green-400" />
              <div>
                <p className="text-xs text-white/60">Balance</p>
                <p className="text-lg font-bold text-white">{formatCurrency(walletBalance)}</p>
              </div>
            </div>

            {/* User */}
            <div className="text-right">
              <p className="text-sm text-white font-medium">{customerName}</p>
              <p className="text-xs text-white/60">#{session?.customerNumber}</p>
            </div>

            {/* Logout */}
            <Button
              variant="ghost"
              size="icon"
              className="text-white/60 hover:text-white"
              onClick={handleLogout}
              title="Sign out"
            >
              <LogOut className="h-5 w-5" />
            </Button>

            {/* Cart Button */}
            {view !== 'cart' && view !== 'checkout' && cartItemCount > 0 && (
              <Button
                variant="blaze"
                className="relative h-12 px-6"
                onClick={() => setView('cart')}
              >
                <ShoppingBag className="h-5 w-5 mr-2" />
                View Cart
                <Badge className="absolute -top-2 -right-2 h-6 w-6 flex items-center justify-center p-0 bg-white text-primary">
                  {cartItemCount}
                </Badge>
              </Button>
            )}
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-12 w-12 text-white/60 animate-spin" />
            </div>
          ) : view === 'categories' ? (
            /* Categories Grid */
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {categories?.map((category) => (
                <button
                  key={category.id}
                  onClick={() => selectCategory(category.id)}
                  className="group bg-white/10 hover:bg-white/20 rounded-2xl p-6 text-center transition-all hover:scale-105"
                >
                  {category.image_url ? (
                    <img
                      src={category.image_url}
                      alt={category.name}
                      className="w-24 h-24 mx-auto mb-4 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 mx-auto mb-4 rounded-xl bg-white/10 flex items-center justify-center">
                      <ShoppingBag className="h-10 w-10 text-white/40" />
                    </div>
                  )}
                  <h3 className="text-xl font-semibold text-white">{category.name}</h3>
                  <p className="text-white/60 text-sm mt-1">
                    {products?.filter(p => p.category_id === category.id && p.is_available).length || 0} items
                  </p>
                </button>
              ))}
            </div>
          ) : view === 'products' ? (
            /* Products Grid */
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredProducts.map((product) => (
                <button
                  key={product.id}
                  onClick={() => addToCart(product)}
                  className="group bg-white/10 hover:bg-white/20 rounded-xl p-4 text-left transition-all hover:scale-[1.02]"
                >
                  {product.image_url ? (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-32 rounded-lg object-cover mb-3"
                    />
                  ) : (
                    <div className="w-full h-32 rounded-lg bg-white/10 flex items-center justify-center mb-3">
                      <ShoppingBag className="h-8 w-8 text-white/40" />
                    </div>
                  )}
                  <h4 className="font-semibold text-white truncate">{product.name}</h4>
                  {product.description && (
                    <p className="text-white/60 text-sm line-clamp-2 mt-1">{product.description}</p>
                  )}
                  <p className="text-lg font-bold text-primary mt-2">{formatCurrency(product.effective_price ?? product.price)}</p>
                </button>
              ))}
              {filteredProducts.length === 0 && (
                <div className="col-span-full text-center py-12 text-white/60">
                  <ShoppingBag className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No items found</p>
                </div>
              )}
            </div>
          ) : view === 'cart' || view === 'checkout' ? (
            /* Cart / Checkout */
            <div className="max-w-2xl mx-auto">
              <div className="bg-white/10 rounded-2xl p-6">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {view === 'cart' ? 'Your Order' : 'Checkout'}
                </h2>

                {cart.length === 0 ? (
                  <div className="text-center py-12 text-white/60">
                    <ShoppingBag className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Your cart is empty</p>
                    <Button
                      variant="outline"
                      className="mt-4 text-white border-white/20"
                      onClick={() => setView('categories')}
                    >
                      Browse Menu
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Cart Items */}
                    <div className="space-y-4 mb-6">
                      {cart.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center gap-4 bg-white/5 rounded-xl p-4"
                        >
                          {item.image_url ? (
                            <img
                              src={item.image_url}
                              alt={item.name}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-white/10 flex items-center justify-center">
                              <ShoppingBag className="h-6 w-6 text-white/40" />
                            </div>
                          )}
                          <div className="flex-1">
                            <h4 className="font-medium text-white">{item.name}</h4>
                            <p className="text-primary font-semibold">
                              {formatCurrency(item.price * item.quantity)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-white"
                              onClick={() => updateQuantity(item.id, -1)}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-8 text-center text-white font-semibold">
                              {item.quantity}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-white"
                              onClick={() => updateQuantity(item.id, 1)}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-400 hover:text-red-300"
                              onClick={() => removeItem(item.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Totals */}
                    <div className="border-t border-white/20 pt-4 space-y-2">
                      <div className="flex justify-between text-white/80">
                        <span>Subtotal</span>
                        <span>{formatCurrency(subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-white/80">
                        <span>Tax ({(taxRate * 100).toFixed(0)}%)</span>
                        <span>{formatCurrency(tax)}</span>
                      </div>
                      <div className="flex justify-between text-xl font-bold text-white pt-2 border-t border-white/20">
                        <span>Total</span>
                        <span>{formatCurrency(total)}</span>
                      </div>
                    </div>

                    {/* Wallet Balance Check */}
                    {!hasSufficientBalance && (
                      <div className="mt-4 p-4 bg-red-500/20 border border-red-500/30 rounded-xl flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-red-300 font-medium">Insufficient Balance</p>
                          <p className="text-red-300/70 text-sm">
                            You need {formatCurrency(total - walletBalance)} more. Please top up your wallet at the counter.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-6 flex gap-4">
                      {view === 'cart' ? (
                        <>
                          <Button
                            variant="outline"
                            className="flex-1 h-14 text-white border-white/20"
                            onClick={() => setView('products')}
                          >
                            Continue Shopping
                          </Button>
                          <Button
                            variant="blaze"
                            className="flex-1 h-14"
                            onClick={() => setView('checkout')}
                            disabled={!hasSufficientBalance}
                          >
                            Proceed to Checkout
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            className="flex-1 h-14 text-white border-white/20"
                            onClick={() => setView('cart')}
                          >
                            Back to Cart
                          </Button>
                          <Button
                            variant="blaze"
                            className={cn(
                              "flex-1 h-14 gap-2",
                              !hasSufficientBalance && "opacity-50 cursor-not-allowed"
                            )}
                            onClick={submitOrder}
                            disabled={isSubmitting || isProcessing || !hasSufficientBalance}
                          >
                            {isSubmitting || isProcessing ? (
                              <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                              <Wallet className="h-5 w-5" />
                            )}
                            Pay {formatCurrency(total)}
                          </Button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

